import { Permission } from '@act/shared/authorization';
import { lazy, ReactNode } from 'react';
import {
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  MANAGER_OPERATING_SYSTEM_FEATURE_FLAG,
  MANAGER_OPERATING_SYSTEM_GUIDE_PATH_PREFIX,
  MANAGER_OPERATING_SYSTEM_PREFIX_PATH,
  MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH,
  MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH,
} from '@act/react-components/manager-operating-system/managerOperatingSystem.constants';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { Routes, SyncHook } from '../routeTypes';
import { DOCKET_PREFIX_PATH } from './DocketRoutes';

const ManagerOperatingSystemNavigationDrawer = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemNavigationDrawer'
  ).then(module => ({
    default: module.ManagerOperatingSystemNavigationDrawer,
  }))
);

const ManagerOperatingSystemYourGuides = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemYourGuides'
  ).then(module => ({
    default: module.ManagerOperatingSystemYourGuides,
  }))
);

const ManagerOperatingSystemTeamWorkflow = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemTeamWorkflow'
  ).then(module => ({
    default: module.ManagerOperatingSystemTeamWorkflow,
  }))
);

const ManagerOperatingSystemGuidePage = lazy(() =>
  import(
    '@act/react-components/manager-operating-system/ManagerOperatingSystemGuidePage'
  ).then(module => ({
    default: module.ManagerOperatingSystemGuidePage,
  }))
);

export {
  MANAGER_OPERATING_SYSTEM_PREFIX_PATH,
  MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH,
  MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH,
};

interface ManagerOperatingSystemPermissionGateProps {
  children: ReactNode;
}

/**
 * Prevents users without Manager Operating System access, or with the feature
 * flag off, from rendering those routes.
 * @param props - Protected route content.
 * @returns Manager Operating System content or a redirect to the guide docket.
 */
export const ManagerOperatingSystemPermissionGate = ({
  children,
}: ManagerOperatingSystemPermissionGateProps) => {
  const canAccessManagerOperatingSystem =
    actingUserAppSelectors.useUserHasPermission(
      Permission.CAN_ACCESS_MANAGER_OPERATING_SYSTEM
    );
  const isManagerOperatingSystemEnabled = useFeatureFlag(
    MANAGER_OPERATING_SYSTEM_FEATURE_FLAG
  );

  return canAccessManagerOperatingSystem &&
    isManagerOperatingSystemEnabled ? (
    <>{children}</>
  ) : (
    <Navigate to={DOCKET_PREFIX_PATH} replace />
  );
};

/**
 * Sends `/manager-operating-system` to Your Guides, keeping MOS filters.
 */
const RedirectToYourGuides = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  return (
    <Navigate
      to={
        search
          ? `${MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH}?${search}`
          : MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH
      }
      replace
    />
  );
};

const routes: Routes = [
  {
    path: MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH,
    element: (
      <ManagerOperatingSystemPermissionGate>
        <ManagerOperatingSystemNavigationDrawer>
          <ManagerOperatingSystemYourGuides />
        </ManagerOperatingSystemNavigationDrawer>
      </ManagerOperatingSystemPermissionGate>
    ),
  },
  {
    path: MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH,
    element: (
      <ManagerOperatingSystemPermissionGate>
        <ManagerOperatingSystemNavigationDrawer>
          <ManagerOperatingSystemTeamWorkflow />
        </ManagerOperatingSystemNavigationDrawer>
      </ManagerOperatingSystemPermissionGate>
    ),
  },
  {
    path: MANAGER_OPERATING_SYSTEM_PREFIX_PATH,
    element: (
      <ManagerOperatingSystemPermissionGate>
        <RedirectToYourGuides />
      </ManagerOperatingSystemPermissionGate>
    ),
  },
  {
    path: `${MANAGER_OPERATING_SYSTEM_GUIDE_PATH_PREFIX}/:guideId`,
    element: (
      <ManagerOperatingSystemPermissionGate>
        <ManagerOperatingSystemNavigationDrawer>
          <ManagerOperatingSystemGuidePage />
        </ManagerOperatingSystemNavigationDrawer>
      </ManagerOperatingSystemPermissionGate>
    ),
  },
];

const useSyncHook: SyncHook = () => {};

const useNavigationHooks = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();

  const withSearch = (pathname: string) =>
    search ? `${pathname}?${search}` : pathname;

  return {
    toManagerOperatingSystem: () =>
      navigate(withSearch(MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH)),
    toManagerOperatingSystemYourGuides: () =>
      navigate(withSearch(MANAGER_OPERATING_SYSTEM_YOUR_GUIDES_PATH)),
    toManagerOperatingSystemTeamWorkflow: () =>
      navigate(withSearch(MANAGER_OPERATING_SYSTEM_TEAM_WORKFLOW_PATH)),
    toManagerOperatingSystemGuide: (guideId: string) =>
      navigate(
        withSearch(`${MANAGER_OPERATING_SYSTEM_GUIDE_PATH_PREFIX}/${guideId}`)
      ),
  };
};

/** Route and navigation configuration for the Manager Operating System. */
export const managerOperatingSystemRouteConfig = {
  routes,
  useSyncHook,
  hideNavigationHeader: true,
  useNavigationHooks,
  publicRoute: false,
};
